/**
 * Reason codes for the «Выставить фактуру» flow (real Fakturownia VAT
 * document issuing).
 *
 * The backend never sends prose — `fakturowniaContributionBillability.reason`
 * and `issueInvoiceForContribution.errorCode` are machine codes that the
 * frontend localizes. This module is the single place that knows what each
 * code MEANS for the operator, so the popup, the header button and any future
 * bulk preview cannot drift apart.
 *
 * Pure by design: no React, no Apollo, no i18n. Mirrors `bulkResult.ts`.
 *
 * FAIL-CLOSED CONTRACT: a code this module does not recognise is treated as
 * BLOCKING. The backend may add a reason at any time; a frontend that
 * silently ignored the unknown code would render a row as billable and invite
 * the operator to create a real fiscal document the server is about to
 * refuse — or worse, to create the wrong one. Unknown ⇒ refuse.
 */

/**
 * Codes the preflight query (`fakturowniaContributionBillability`) can
 * return in `reason` when `billable` is false.
 */
export const PREFLIGHT_REASONS = [
  'INTEGRATION_DISABLED',
  'EXEMPT_REASON_NOT_CONFIGURED',
  'AGREEMENT_FREE_OF_CHARGE',
  'AGREEMENT_STATE_PREFINANCED',
  'AGREEMENT_TERMINATED',
  'NO_UNPAID_CONTRIBUTION',
  'CONTRIBUTION_ALREADY_INVOICED',
  'CONTRIBUTION_PARTIALLY_COVERED',
  'MISSING_AMOUNT',
  'MISSING_CURRENCY',
  // ТЗ 0076: код остаётся прежним — он хранится в
  // `FakturowniaBillingJobRow.reason`. Смысл шире имени: нет подписанного
  // договора с реквизитами НИ в ERP (Documenso), НИ в PandaDoc. Меняется
  // только текст (`pages.fakturownia.issue.reasons.NO_PANDADOC_DOCUMENT`).
  'NO_PANDADOC_DOCUMENT',
  'MISSING_BUYER_EMAIL',
  'MISSING_BUYER_IDENTITY',
  'MISSING_BUYER_ADDRESS',
  'ISSUE_IN_FLIGHT',
  // ТЗ 0101 — проформы. Оба «проформенных» кода снимает `allowReissue`, но
  // этот UI его не шлёт: для оператора они блокирующие.
  'CONTRIBUTION_HAS_OPEN_PROFORMA',
  'CONTRIBUTION_HAS_PROFORMA',
  'UNSUPPORTED_KIND',
] as const;

/**
 * Codes only the mutation can return — they describe how the POST to
 * Fakturownia went, not whether the installment was billable to begin with.
 */
export const MUTATION_REASONS = [
  'FAKTUROWNIA_REJECTED',
  'FAKTUROWNIA_AMBIGUOUS',
  'CREATED_BUT_NOT_LINKED',
  'AGREEMENT_NOT_FOUND',
  'CONTRIBUTION_NOT_FOUND',
  // ТЗ 0101: «Выставить VAT» из проформы и привязка проформы к взносу.
  'VAT_ALREADY_ISSUED',
  'NOT_A_PROFORMA',
  'PROFORMA_REJECTED',
  'STUDENT_MISMATCH',
  // Валюта документа ≠ валюта договора: привязка счёта / проформы и «VAT из
  // проформы» отказывают — такой документ не учитывается в покрытии взноса.
  'CURRENCY_MISMATCH',
] as const;

export const INVOICE_ISSUE_REASONS = [
  ...PREFLIGHT_REASONS,
  ...MUTATION_REASONS,
] as const;

export type IInvoiceIssueReason = typeof INVOICE_ISSUE_REASONS[number];

/**
 * `blocking`    — no operator action in this UI clears it; the document
 *                 cannot be issued.
 * `overridable` — the backend's `allowReissue: true` bypasses it. Note this
 *                 does NOT make the installment billable by default; it only
 *                 marks the code as clearable by an explicit override.
 */
export type IInvoiceIssueSeverity = 'blocking' | 'overridable';

/**
 * Severity per known code.
 *
 * Exactly ONE code is overridable: `CONTRIBUTION_ALREADY_INVOICED`. The
 * backend is explicit that `allow_reissue` exists for «the document was
 * wrong», not «take the money twice» — so `CONTRIBUTION_PARTIALLY_COVERED`
 * stays blocking even though it looks adjacent.
 */
export const REASON_SEVERITY: Readonly<
  Record<IInvoiceIssueReason, IInvoiceIssueSeverity>
> = {
  INTEGRATION_DISABLED: 'blocking',
  EXEMPT_REASON_NOT_CONFIGURED: 'blocking',
  AGREEMENT_FREE_OF_CHARGE: 'blocking',
  AGREEMENT_STATE_PREFINANCED: 'blocking',
  AGREEMENT_TERMINATED: 'blocking',
  NO_UNPAID_CONTRIBUTION: 'blocking',
  CONTRIBUTION_ALREADY_INVOICED: 'overridable',
  CONTRIBUTION_PARTIALLY_COVERED: 'blocking',
  MISSING_AMOUNT: 'blocking',
  MISSING_CURRENCY: 'blocking',
  NO_PANDADOC_DOCUMENT: 'blocking',
  MISSING_BUYER_EMAIL: 'blocking',
  MISSING_BUYER_IDENTITY: 'blocking',
  MISSING_BUYER_ADDRESS: 'blocking',
  ISSUE_IN_FLIGHT: 'blocking',
  FAKTUROWNIA_REJECTED: 'blocking',
  FAKTUROWNIA_AMBIGUOUS: 'blocking',
  CREATED_BUT_NOT_LINKED: 'blocking',
  AGREEMENT_NOT_FOUND: 'blocking',
  CONTRIBUTION_NOT_FOUND: 'blocking',
  CONTRIBUTION_HAS_OPEN_PROFORMA: 'blocking',
  CONTRIBUTION_HAS_PROFORMA: 'blocking',
  UNSUPPORTED_KIND: 'blocking',
  VAT_ALREADY_ISSUED: 'blocking',
  NOT_A_PROFORMA: 'blocking',
  PROFORMA_REJECTED: 'blocking',
  STUDENT_MISMATCH: 'blocking',
  CURRENCY_MISMATCH: 'blocking',
};

/**
 * Ключ текста кода мутации Fakturownia (`pages.fakturownia.issue.reasons.*`).
 * У «Выставить VAT из проформы» (`context: 'vat'`) для `CURRENCY_MISMATCH`
 * своя формулировка: там не «привязать нельзя», а «VAT не выставить».
 */
export const fakturowniaReasonKey = (code: string, context?: 'vat'): string =>
  `pages.fakturownia.issue.reasons.${
    context === 'vat' && code === 'CURRENCY_MISMATCH'
      ? 'CURRENCY_MISMATCH_VAT'
      : code
  }`;

/** Type guard — `true` only for codes this build knows about. */
export const isKnownReason = (code: string): code is IInvoiceIssueReason =>
  Object.prototype.hasOwnProperty.call(REASON_SEVERITY, code);

/**
 * Severity of one raw code. **Unknown codes are `blocking`** — see the
 * fail-closed contract at the top of this file.
 */
export const severityOf = (code: string): IInvoiceIssueSeverity =>
  isKnownReason(code) ? REASON_SEVERITY[code] : 'blocking';

/**
 * Drop nullish / blank entries and de-duplicate, preserving first-seen order.
 * The backend sends a single `reason`, but the UI aggregates several sources
 * (preflight reason + mutation errorCode), so the helpers all take a list.
 */
export const normalizeReasons = (
  reasons: ReadonlyArray<string | null | undefined>,
): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  reasons.forEach((raw) => {
    const code = (raw || '').trim();
    if (!code || seen.has(code)) return;
    seen.add(code);
    out.push(code);
  });
  return out;
};

/**
 * The subset that no override can clear. Unknown codes are included
 * (fail-closed), so a future backend reason can never be silently dropped.
 */
export const blockingReasons = (
  reasons: ReadonlyArray<string | null | undefined>,
): string[] =>
  normalizeReasons(reasons).filter((code) => severityOf(code) === 'blocking');

/**
 * `true` only when NOTHING stands in the way — an empty reason list.
 *
 * An `overridable` reason still returns `false`: the default issue path does
 * not send `allowReissue`, so «already invoiced» is not billable as-is. The
 * distinction lives in `blockingReasons` (which such a code is absent from),
 * so a caller offering a reissue affordance can tell the two apart.
 */
export const isBillable = (
  reasons: ReadonlyArray<string | null | undefined>,
): boolean => normalizeReasons(reasons).length === 0;

/**
 * Could a real fiscal document exist in Fakturownia despite the failure?
 *
 * Two distinct cases, and `documentCreated` alone does NOT cover both:
 *   - `documentCreated === true` (`CREATED_BUT_NOT_LINKED`) — the document
 *     definitely exists; mirroring into the ERP failed afterwards.
 *   - `FAKTUROWNIA_AMBIGUOUS` — a timeout / connection reset AFTER the POST
 *     left the outcome unknown. The backend returns `documentCreated=false`
 *     here because it genuinely does not know, and it deliberately lets the
 *     lock expire instead of reopening the attempt.
 *
 * Both must send the operator to Fakturownia to look. Nothing in this system
 * deletes such a document.
 */
export const documentMayExist = (
  errorCode: string | null | undefined,
  documentCreated: boolean | null | undefined,
): boolean => documentCreated === true || errorCode === 'FAKTUROWNIA_AMBIGUOUS';

/**
 * Human chip for the VAT rate the backend will send (preflight `taxRate`).
 * A numeric rate renders as «VAT 23%»; an exempt code (zw/np/oo) as «VAT · zw».
 */
export const formatTaxRate = (rate: string): string => {
  const value = (rate || '').trim();
  if (!value) return 'VAT';
  return /^\d+$/.test(value) ? `VAT ${value}%` : `VAT · ${value}`;
};
